import React from "react";
import { useDisplayOptionContext } from "@/hooks/useDisplayOptionContext";
import { useUnitItemContext } from "@/hooks/useUnitItemContext";
import {
    QuoteButton,
    RentButton,
    ReserveButton,
    SelectButton,
} from "@/components/units/action";
import { QuoteType } from "@/lib/utils";
import { useRentalAction } from "@/hooks/useRentalAction";
import { useFacility } from "@/hooks/useFacility";
import { CallForAvailability } from "@/components/units/action/CallForAvailability";
import { useTriggerButtonVisibility } from "@/hooks/useTriggerButtonVisibility";
import { useReservationAction } from "@/hooks/useReservationAction";

export const TriggerButtons = React.forwardRef<
    HTMLDivElement | HTMLButtonElement
>((_props, ref) => {
    const { unitGroup } = useUnitItemContext();
    const displayOptions = useDisplayOptionContext();
    const { openVbp, openRentalFunnel } = useRentalAction(
        unitGroup.representative_unit.id,
    );
    const { disableSoftReservation } = displayOptions;
    const { facility } = useFacility(unitGroup.representative_unit.facility_id);
    const {
        hasMultipleSubGroups,
        hasAvailableUnits,
        canReserve,
        canRent,
        shouldShowSelectButton,
    } = useTriggerButtonVisibility();
    const { openReservationFunnel, label } = useReservationAction(
        unitGroup.representative_unit.id,
    );

    const phone = facility?.phone || "";
    const quoteType = facility?.settings.quote_type;

    const handleRentalClick = () => {
        if (hasMultipleSubGroups) {
            return openVbp();
        } else {
            return openRentalFunnel();
        }
    };

    /*
     * If the unit group has available units, we will either show the
     * Select button, or the individual action buttons.
     */
    if (hasAvailableUnits) {
        if (shouldShowSelectButton()) {
            return <SelectButton ref={ref as React.Ref<HTMLButtonElement>} />;
        }
        return (
            <div ref={ref as React.Ref<HTMLDivElement>}>
                {canRent && (
                    <RentButton handleRentalClick={handleRentalClick} />
                )}
                {canReserve && (
                    <ReserveButton
                        onReservationClick={openReservationFunnel}
                        label={label}
                    />
                )}
                {/* Safety fallback. */}
                {!canRent && !canReserve && (
                    <>
                        {!disableSoftReservation &&
                        quoteType === QuoteType.RESERVE ? (
                            <QuoteButton
                                ref={ref as React.Ref<HTMLButtonElement>}
                                unitId={unitGroup.representative_unit.id}
                            />
                        ) : (
                            <CallForAvailability phone={phone} />
                        )}
                    </>
                )}
            </div>
        );
    }

    /*
     * No available units. We will either show the Quote button,
     * or Call for Availability.
     *
     * At this point, 'reservation_type' is NOT 'click_to_call', and
     * there are no available units.
     */
    if (disableSoftReservation) {
        console.log(
            "Soft reservation disabled, no available units. Showing Call for Availability",
        );
        return (
            <CallForAvailability
                ref={ref as React.Ref<HTMLDivElement>}
                phone={phone}
            />
        );
    } else {
        console.log(
            quoteType === QuoteType.RESERVE
                ? "Quote type is reserve, showing Quote button"
                : "Quote type is call, showing Call for Availability",
        );
        return quoteType === QuoteType.RESERVE ? (
            <QuoteButton
                ref={ref as React.Ref<HTMLButtonElement>}
                unitId={unitGroup.representative_unit.id}
            />
        ) : (
            <CallForAvailability
                ref={ref as React.Ref<HTMLDivElement>}
                phone={phone}
            />
        );
    }
});

TriggerButtons.displayName = "TriggerButtons";
